import React, { useMemo, useState } from 'react';
import { Box, Popover, Typography } from '@mui/material';
import SourceChip from '../../../../capability-setup/SourceChip';
import { sourceOf } from '../../../../capability-setup/capabilitySource';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import DosenTooltip from '../../../../../../../components/ui/DosenTooltip';
import { Bone, fadeInSx } from '../../../../../../../components/ui/DashboardSkeletons';
import PinnedScrollbar, { HIDDEN_X_SCROLLBAR } from '../../../../../../../components/ui/PinnedScrollbar';
import { CapabilityScoreBreakdown } from '../../learning-lifecycle/TeamCapabilityScore';
import type { CapabilityPair } from '../../learning-lifecycle/teamCapability';
import {
  NOT_APPLICABLE,
  ROWS_LABEL,
  VIEW_BY,
  type MatrixCell,
  type MatrixColumn,
  type MatrixRow,
  type RowsBy
} from './insightsData';
import { MOBILE_SX, useIsMobileApp } from '../../../../../../../mobile/mobileApp';

const FONT = 'Inter, sans-serif';
const GRID_LINE = '1px solid #262626';
const HEAD_LINE = '1px solid #2A2A2A';
const SCORE_LINE = '1px solid rgba(247, 148, 32, 0.45)';
const SURFACE = '#1C1C1C';
const FIRST_COL = 220;
const COUNT_COL = 112;
const SCORE_COL = 176;
// Wide enough for a name like "Discovery Questioning" on one line; a longer one is cut with its whole on hover.
const COL = 204;
// The header row's own fill, a shade off the table's.
const HEAD_FILL = '#232323';

const HEAD_TEXT = {
  fontFamily: FONT,
  fontSize: 11,
  fontWeight: 600,
  lineHeight: 1.3,
  textTransform: 'uppercase',
  color: '#868686'
} as const;
const CENTRED = { display: 'flex', alignItems: 'center', justifyContent: 'center' } as const;

const CELL_TEXT = (cell: MatrixCell) => ({
  fontFamily: FONT,
  fontSize: cell.quiet ? 10.5 : 11,
  fontWeight: cell.quiet ? 500 : 700,
  letterSpacing: cell.quiet ? 0 : '0.04em',
  textTransform: cell.quiet ? 'none' : 'uppercase',
  textAlign: 'center',
  color: cell.color
});

/**
 * A value in a table: a level or a % in its colour, or a quiet "N/A" / "Not
 * Assessed Yet". A % with its scores behind it is a button that opens the
 * breakdown.
 */
const CellText: React.FC<{
  cell: MatrixCell;
  onOpen?: (anchor: HTMLElement, pairs: CapabilityPair[], versus?: MatrixCell['versus']) => void;
}> = ({ cell, onOpen }) => {
  const { pairs, versus } = cell;
  const isMobileApp = useIsMobileApp();
  // Behavior: hovering says what Dosen has for this cell beside what was observed. On the phone a
  // tap does; a % that opens its breakdown says it there instead, so one tap opens one thing.
  const compared = (child: React.ReactElement) =>
    versus && !(isMobileApp && pairs && onOpen) ? (
      <DosenTooltip
        tapOnPhone
        placement="bottom"
        title={
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px', minWidth: 190 }}>
            {[
              { label: 'On Dosen', ...versus.dosen },
              { label: 'Observed Behavior', ...versus.observed }
            ].map((line) => (
              <Box key={line.label} sx={{ display: 'flex', justifyContent: 'space-between', gap: '16px' }}>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#B6B6B6' }}>{line.label}</Typography>
                <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: line.color }}>
                  {line.text}
                </Typography>
              </Box>
            ))}
          </Box>
        }
      >
        {child}
      </DosenTooltip>
    ) : (
      child
    );
  if (!pairs || !onOpen) {
    return compared(
      <Typography
        sx={{
          ...CELL_TEXT(cell),
          // Underlined where there is something to compare on hover.
          ...(versus ? { textDecoration: 'underline', textUnderlineOffset: '4px', cursor: 'default' } : null)
        }}
      >
        {cell.text}
      </Typography>
    );
  }
  return compared(
    <Box
      component="button"
      type="button"
      aria-label={`${cell.text}: see the breakdown`}
      onClick={(e: React.MouseEvent<HTMLElement>) => onOpen(e.currentTarget, pairs, versus)}
      sx={{
        ...CELL_TEXT(cell),
        p: 0,
        border: 'none',
        bgcolor: 'transparent',
        cursor: 'pointer',
        textDecoration: 'underline dotted',
        textUnderlineOffset: '4px',
        '&:hover': { textDecorationStyle: 'solid' }
      }}
    >
      {cell.text}
    </Box>
  );
};

/** Employees or Assessed for a row: a count, or by employee "N/A" and a tick or a cross. */
const CountCell: React.FC<{ value?: number; as: 'count' | 'na' | 'tick' }> = ({ value, as }) => {
  if (as === 'tick') {
    return value ? (
      <CheckIcon aria-label="Assessed" sx={{ fontSize: 17, color: '#22C55E' }} />
    ) : (
      <CloseIcon aria-label="Not assessed yet" sx={{ fontSize: 17, color: '#FF6A55' }} />
    );
  }
  return (
    <Typography sx={{ fontFamily: FONT, fontSize: 12.5, fontWeight: 600, color: as === 'na' ? '#565656' : '#B6B6B6' }}>
      {as === 'na' ? 'N/A' : value ?? '-'}
    </Typography>
  );
};

// ---- Ordering ---------------------------------------------------------------
// The rows can be ordered by any column, and the capability columns by any row:
// most to least, then least to most. Anything without a value goes last.

type SortDir = 'desc' | 'asc';
type Sort = { by: 'column' | 'row'; key: string; dir: SortDir } | null;
type Sortable = number | string | undefined;

const compare = (a: Sortable, b: Sortable, dir: SortDir) => {
  if (a === undefined || b === undefined) return a === b ? 0 : a === undefined ? 1 : -1;
  const order = typeof a === 'string' ? a.localeCompare(String(b)) : a - (b as number);
  return dir === 'asc' ? order : -order;
};
const nextSort = (sort: Sort, by: 'column' | 'row', key: string): Sort => {
  if (sort?.by !== by || sort.key !== key) return { by, key, dir: 'desc' };
  // The third click clears the order.
  return sort.dir === 'desc' ? { by, key, dir: 'asc' } : null;
};
// How the table is ordered when it opens: as it comes, team by team.
const OPENING_SORT: Sort = null;

/** The arrow on a header (orders the rows) or beside a row's name (orders the columns). */
const SortMark: React.FC<{ dir: SortDir | null; across?: boolean; label: string; onClick: () => void }> = ({
  dir,
  across = false,
  label,
  onClick
}) => {
  const Icon = across
    ? dir === 'asc'
      ? ArrowBackIcon
      : ArrowForwardIcon
    : dir === 'desc'
    ? ArrowDownwardIcon
    : dir === 'asc'
    ? ArrowUpwardIcon
    : UnfoldMoreIcon;
  const action =
    dir === 'asc'
      ? `Clear the order ${label}`
      : `Order ${label}, ${dir === 'desc' ? 'least to most' : 'most to least'}`;
  return (
    <DosenTooltip title={action} placement="bottom" enterDelay={300}>
      <Box
        component="button"
        type="button"
        aria-label={action}
        onClick={onClick}
        sx={{
          flexShrink: 0,
          width: 18,
          height: 18,
          p: 0,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: 'none',
          bgcolor: 'transparent',
          cursor: 'pointer',
          color: dir ? '#F79420' : '#868686',
          '&:hover': { color: dir ? '#F79420' : '#B6B6B6' }
        }}
      >
        <Icon sx={{ fontSize: 14 }} />
      </Box>
    </DosenTooltip>
  );
};

/** A row's name, with what it belongs to beneath. */
const NameCell: React.FC<{ label: string; sub?: string }> = ({ label, sub }) => (
  <DosenTooltip title={sub ? `${label} · ${sub}` : label} placement="bottom-start" enterDelay={400}>
    <Box sx={{ minWidth: 0 }}>
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: 13,
          fontWeight: 600,
          color: '#FDFDFD',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap'
        }}
      >
        {label}
      </Typography>
      {sub && (
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: 11,
            color: '#868686',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap'
          }}
        >
          {sub}
        </Typography>
      )}
    </Box>
  </DosenTooltip>
);

/**
 * View By: Employee | Role | Team, what the table's rows are. With the whole
 * Organization in view there is one row, so the choices are greyed out and
 * hovering says how to get them back.
 */
const ViewBy: React.FC<{ value: RowsBy; locked: boolean; onChange: (next: RowsBy) => void }> = ({
  value,
  locked,
  onChange
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', flexShrink: 0 }}>
    <Typography sx={{ ...HEAD_TEXT, fontWeight: 700, textTransform: 'none', [MOBILE_SX]: { display: 'none' } }}>
      View by
    </Typography>
    <DosenTooltip
      title={locked ? 'Select one or more Teams to view by Employee, Role, or Team.' : ''}
      placement="bottom-start"
    >
      <Box
        role="tablist"
        aria-label="View by"
        aria-disabled={locked}
        sx={{ display: 'inline-flex', border: HEAD_LINE, borderRadius: '4px', cursor: locked ? 'not-allowed' : 'auto' }}
      >
        {VIEW_BY.map((option, i) => {
          const on = !locked && option.key === value;
          return (
            <Box
              key={option.key}
              component="button"
              type="button"
              role="tab"
              aria-selected={on}
              disabled={locked}
              onClick={() => onChange(option.key)}
              sx={{
                height: 28,
                px: '12px',
                border: 'none',
                borderLeft: i > 0 ? HEAD_LINE : 'none',
                bgcolor: on ? 'rgba(247, 148, 32, 0.12)' : 'transparent',
                color: on ? '#F79420' : '#B6B6B6',
                fontFamily: FONT,
                fontSize: 12,
                fontWeight: 600,
                cursor: 'pointer',
                '&:hover': { color: on ? '#F79420' : '#FDFDFD' },
                // Greyed out; the hover is the wrapper's, so the tooltip still shows.
                '&:disabled': { color: '#565656', pointerEvents: 'none' },
                [MOBILE_SX]: { height: 36, px: '9px', fontSize: 12.5 }
              }}
            >
              {option.label}
            </Box>
          );
        })}
      </Box>
    </DosenTooltip>
  </Box>
);

/** A row's count on the phone: Learners or Assessed and its figure. */
interface PhoneCount {
  label: string;
  value?: number;
  as: 'count' | 'na' | 'tick';
}
const RowCount: React.FC<PhoneCount> = ({ label, value, as }) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
    <Typography sx={{ ...HEAD_TEXT, fontSize: 10.5 }}>{label}</Typography>
    <CountCell value={value} as={as} />
  </Box>
);

/**
 * The table on the phone: a card per row. Its name in full, its score (which opens the breakdown,
 * as in the table), its counts, then each capability or measure that applies to it with its value.
 * The capabilities that are not the row's (N/A) are left out; Behavior shows what Dosen has beside
 * what was observed, which the table shows on hover.
 */
const MatrixCards: React.FC<{
  rowsBy: RowsBy;
  /** What sits under the score. Left out, nothing does. */
  scoreCaption?: string;
  assessedLabel?: string;
  columns: MatrixColumn[];
  rows: MatrixRow[];
  onOpen?: (anchor: HTMLElement, pairs: CapabilityPair[], versus?: MatrixCell['versus'], counts?: PhoneCount[]) => void;
}> = ({ rowsBy, scoreCaption, assessedLabel, columns, rows, onOpen }) => (
  <Box sx={{ display: 'flex', flexDirection: 'column', gap: '10px', ...fadeInSx }}>
    {rows.map((row, r) => {
      const lines = columns
        .map((column, c) => ({ column, cell: row.cells[c] }))
        .filter(({ cell }) => cell && !(cell.quiet && cell.text === NOT_APPLICABLE.text));
      const counts = [
        { label: 'Learners', value: row.employees, as: rowsBy === 'employee' ? ('na' as const) : ('count' as const) },
        ...(assessedLabel
          ? [
              {
                label: assessedLabel,
                value: row.assessed,
                as: rowsBy === 'employee' ? ('tick' as const) : ('count' as const)
              }
            ]
          : [])
        // Someone assessed needs no tick: only the cross for someone who is not says anything.
      ].filter((count) => count.as !== 'na' && !(count.as === 'tick' && count.value));
      return (
        // Someone in two teams is two rows with one id; the position keeps the keys unique.
        <Box
          key={`${r}|${row.id}`}
          sx={{
            border: GRID_LINE,
            borderRadius: '6px',
            bgcolor: '#161616',
            p: '12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography sx={{ fontFamily: FONT, fontSize: 15, fontWeight: 600, lineHeight: 1.3, color: '#FDFDFD' }}>
                {row.label}
              </Typography>
              {row.sub && (
                <Typography sx={{ fontFamily: FONT, fontSize: 12, lineHeight: 1.4, color: '#868686', mt: '2px' }}>
                  {row.sub}
                </Typography>
              )}
            </Box>
            {/* A score that opens its breakdown carries the counts there; otherwise they stay on the card. */}
            {counts.length > 0 && !(row.score.pairs && onOpen) && (
              <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}>
                {counts.map((count) => (
                  <RowCount key={count.label} {...count} />
                ))}
              </Box>
            )}
            <Box sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px' }}>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '6px' }}>
                {scoreCaption && <Typography sx={{ ...HEAD_TEXT, fontSize: 10 }}>{scoreCaption}:</Typography>}
                {/* "Not assessed" stays quiet, leaving the name its room. */}
                <Box sx={row.score.quiet ? undefined : { '& > *': { fontSize: '15px !important' } }}>
                  <CellText
                    cell={row.score}
                    onOpen={onOpen && ((anchor, pairs, versus) => onOpen(anchor, pairs, versus, counts))}
                  />
                </Box>
              </Box>
            </Box>
          </Box>
          {lines.length > 0 && (
            <Box sx={{ borderTop: GRID_LINE, display: 'flex', flexDirection: 'column' }}>
              {lines.map(({ column, cell }) => (
                <Box
                  key={column.key}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '12px',
                    py: '8px',
                    '&:not(:last-of-type)': { borderBottom: GRID_LINE }
                  }}
                >
                  <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: '6px' }}>
                    {/* One line: a name too long for it is cut, and a tap shows it whole. */}
                    <DosenTooltip title={column.name} placement="bottom-start" enterTouchDelay={0} leaveTouchDelay={4000}>
                      <Typography
                        sx={{
                          minWidth: 0,
                          fontFamily: FONT,
                          fontSize: 13,
                          lineHeight: 1.35,
                          color: '#B6B6B6',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {column.name}
                      </Typography>
                    </DosenTooltip>
                    {column.source && <SourceChip compact source={sourceOf(column)} />}
                  </Box>
                  <Box
                    sx={{ flexShrink: 0, display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '2px' }}
                  >
                    <CellText cell={cell} onOpen={onOpen} />
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </Box>
      );
    })}
  </Box>
);

/**
 * The Insights table: rows by employee, role or team (the org, with the
 * Organization in view); how many employees and scores each row covers and
 * its score, pinned beside the name; then a column for each capability (or
 * measure). It takes the height it is given and scrolls inside it, the header
 * and the pinned columns staying put.
 */
const LevelMatrix: React.FC<{
  rowsBy: RowsBy;
  /** The whole Organization is in view: one row, and View By greyed out. */
  locked: boolean;
  onRowsByChange: (next: RowsBy) => void;
  /** Phone: the teams filter, in the top row beside View By. */
  phoneFilter?: React.ReactNode;
  /** The score column: "Capability Score". */
  scoreLabel: string;
  /** Phone: no label under a card's score. */
  noPhoneScoreCaption?: boolean;
  /** The count the score is out of: "Assessed". Left out, there is no such column. */
  assessedLabel?: string;
  columns: MatrixColumn[];
  rows: MatrixRow[];
  /** What the table says with nothing to show. */
  empty: string;
  /** The data is still on its way: a dark placeholder shaped like the table stands in. */
  loading?: boolean;
  /** What a % opens when clicked: the breakdown of the scores behind it. */
  breakdown?: { teams: Array<{ id: string; name: string }>; resultLabel: string };
}> = ({
  rowsBy,
  locked,
  onRowsByChange,
  phoneFilter,
  scoreLabel,
  noPhoneScoreCaption = false,
  assessedLabel,
  columns: allColumns,
  rows: allRows,
  empty,
  loading = false,
  breakdown
}) => {
  const isMobileApp = useIsMobileApp();
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  const [sort, setSort] = useState<Sort>(OPENING_SORT);
  const [opened, setOpened] = useState<{
    anchor: HTMLElement;
    pairs: CapabilityPair[];
    versus?: MatrixCell['versus'];
    counts?: PhoneCount[];
  } | null>(null);
  const onOpen = breakdown
    ? (anchor: HTMLElement, pairs: CapabilityPair[], versus?: MatrixCell['versus'], counts?: PhoneCount[]) =>
        setOpened({ anchor, pairs, versus, counts })
    : undefined;

  // The capability columns in order (by a row's values, if one is picked), and each row's cells to match.
  const { columns, rows } = useMemo(() => {
    let order = allColumns.map((_, i) => i);
    if (sort?.by === 'row') {
      const cells = allRows.find((r) => r.id === sort.key)?.cells;
      if (cells) order = [...order].sort((a, b) => compare(cells[a]?.value, cells[b]?.value, sort.dir));
    }
    let ordered = allRows;
    if (sort?.by === 'column') {
      const at = allColumns.findIndex((c) => c.key === sort.key);
      const valueOf = (row: MatrixRow): Sortable => {
        if (sort.key === 'name') return row.label.toLowerCase();
        if (sort.key === 'employees') return row.employees;
        if (sort.key === 'assessed') return row.assessed;
        if (sort.key === 'score') return row.score.value;
        return row.cells[at]?.value;
      };
      ordered = [...allRows].sort((a, b) => compare(valueOf(a), valueOf(b), sort.dir));
    }
    return {
      columns: order.map((i) => allColumns[i]),
      rows: ordered.map((row) => ({ ...row, cells: order.map((i) => row.cells[i]) }))
    };
  }, [allColumns, allRows, sort]);
  const columnDir = (key: string) => (sort?.by === 'column' && sort.key === key ? sort.dir : null);
  const sortColumn = (key: string, label: string) => (
    <SortMark dir={columnDir(key)} label={`by ${label}`} onClick={() => setSort((s) => nextSort(s, 'column', key))} />
  );

  // The pinned columns after the name, left to right, each with where it sticks.
  const pinned = [
    { key: 'employees', label: 'Learners', width: COUNT_COL },
    ...(assessedLabel ? [{ key: 'assessed', label: assessedLabel, width: COUNT_COL }] : []),
    { key: 'score', label: scoreLabel, width: SCORE_COL }
  ].map((column, i, all) => ({
    ...column,
    left: FIRST_COL + all.slice(0, i).reduce((sum, c) => sum + c.width, 0),
    last: i === all.length - 1
  }));
  const pinnedWidth = pinned.reduce((sum, c) => sum + c.width, 0);
  const pinnedSx = (column: (typeof pinned)[number]) => ({
    ...CENTRED,
    position: 'sticky' as const,
    left: column.left,
    bgcolor: SURFACE,
    borderLeft: GRID_LINE,
    // The score closes the pinned block with a brandy rule.
    ...(column.last ? { borderRight: SCORE_LINE } : null)
  });

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: '10px' }}>
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          [MOBILE_SX]: { gap: '6px' }
        }}
      >
        <ViewBy value={rowsBy} locked={locked} onChange={onRowsByChange} />
        {phoneFilter && <Box sx={{ minWidth: 0 }}>{phoneFilter}</Box>}
        {/* The phone has no header row to order by: the score's order sits here. */}
        {isMobileApp && rows.length > 1 && (
          <Box sx={{ flexShrink: 0, ml: 'auto', '& > button': { width: 32, height: 36 } }}>
            {sortColumn('score', scoreLabel)}
          </Box>
        )}
      </Box>
      {loading && isMobileApp ? (
        <Box
          aria-busy="true"
          aria-label="Loading Insights"
          sx={{ display: 'flex', flexDirection: 'column', gap: '10px' }}
        >
          {[0, 1, 2].map((i) => (
            <Bone key={i} height={120} />
          ))}
        </Box>
      ) : loading ? (
        <Box
          aria-busy="true"
          aria-label="Loading Insights"
          sx={{
            border: GRID_LINE,
            borderRadius: '4px',
            p: '10px 12px',
            display: 'flex',
            flexDirection: 'column',
            gap: '10px'
          }}
        >
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <Box key={i} sx={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
              <Bone width={FIRST_COL - 24} height={i === 0 ? 14 : 18} />
              {[0, 1, 2, 3, 4].map((c) => (
                <Bone key={c} width={c < 2 ? COUNT_COL - 24 : COL - 40} height={i === 0 ? 14 : 18} />
              ))}
            </Box>
          ))}
        </Box>
      ) : rows.length === 0 || columns.length === 0 ? (
        <Typography sx={{ fontFamily: FONT, fontSize: 13, color: '#868686' }}>{empty}</Typography>
      ) : isMobileApp ? (
        <MatrixCards
          rowsBy={rowsBy}
          scoreCaption={noPhoneScoreCaption ? undefined : scoreLabel}
          assessedLabel={assessedLabel}
          columns={columns}
          rows={rows}
          onOpen={onOpen}
        />
      ) : (
        <Box
          sx={{
            flex: '0 1 auto',
            minHeight: 0,
            display: 'flex',
            flexDirection: 'column',
            border: GRID_LINE,
            borderRadius: '4px',
            overflow: 'hidden',
            ...fadeInSx
          }}
        >
          <Box ref={setScroller} sx={{ flex: '0 1 auto', minHeight: 0, overflow: 'auto', ...HIDDEN_X_SCROLLBAR }}>
            <Box
              role="table"
              sx={{
                display: 'grid',
                gridTemplateColumns: `${FIRST_COL}px ${pinned.map((c) => `${c.width}px`).join(' ')} repeat(${
                  columns.length
                }, minmax(${COL}px, 1fr))`,
                minWidth: FIRST_COL + pinnedWidth + columns.length * COL
              }}
            >
              {/* Header */}
              <Box
                sx={{
                  position: 'sticky',
                  top: 0,
                  left: 0,
                  zIndex: 3,
                  bgcolor: HEAD_FILL,
                  borderBottom: HEAD_LINE,
                  p: '9px 12px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px'
                }}
              >
                <Typography sx={HEAD_TEXT}>{ROWS_LABEL[rowsBy]}</Typography>
                {sortColumn('name', ROWS_LABEL[rowsBy])}
              </Box>
              {pinned.map((column) => (
                <Box
                  key={column.key}
                  sx={{
                    ...pinnedSx(column),
                    top: 0,
                    zIndex: 3,
                    borderBottom: HEAD_LINE,
                    bgcolor: HEAD_FILL,
                    p: '9px 8px',
                    gap: '2px'
                  }}
                >
                  <Typography sx={{ ...HEAD_TEXT, whiteSpace: 'nowrap', ...(column.last ? { color: '#FDFDFD' } : null) }}>
                    {column.label}
                  </Typography>
                  {sortColumn(column.key, column.label)}
                </Box>
              ))}
              {columns.map((column, c) => (
                <Box
                  key={column.key}
                  sx={{
                    ...CENTRED,
                    flexDirection: 'column',
                    position: 'sticky',
                    top: 0,
                    zIndex: 2,
                    bgcolor: HEAD_FILL,
                    borderBottom: HEAD_LINE,
                    borderLeft: c > 0 ? GRID_LINE : 'none',
                    p: '9px 8px',
                    minWidth: 0
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '2px', maxWidth: '100%', minWidth: 0 }}>
                    {/* One line: a name too long for the cell is cut, and shown whole on hover. */}
                    <DosenTooltip title={column.name} placement="bottom" enterDelay={300}>
                      <Typography
                        sx={{
                          ...HEAD_TEXT,
                          minWidth: 0,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap'
                        }}
                      >
                        {column.name}
                      </Typography>
                    </DosenTooltip>
                    {column.source && <SourceChip compact source={sourceOf(column)} />}
                    {sortColumn(column.key, column.name)}
                  </Box>
                </Box>
              ))}
  
              {/* Rows */}
              {rows.map((row, r) => {
                const rule = r < rows.length - 1 ? GRID_LINE : 'none';
                return (
                  <React.Fragment key={row.id}>
                    <Box
                      sx={{
                        position: 'sticky',
                        left: 0,
                        zIndex: 1,
                        bgcolor: SURFACE,
                        borderBottom: rule,
                        p: '9px 12px',
                        minWidth: 0,
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px'
                      }}
                    >
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <NameCell label={row.label} sub={row.sub} />
                      </Box>
                      <SortMark
                        across
                        dir={sort?.by === 'row' && sort.key === row.id ? sort.dir : null}
                        label={`the columns by ${row.label}`}
                        onClick={() => setSort((s) => nextSort(s, 'row', row.id))}
                      />
                    </Box>
                    {pinned.map((column) => (
                      <Box key={column.key} sx={{ ...pinnedSx(column), zIndex: 1, borderBottom: rule, p: '9px 6px' }}>
                        {column.key === 'score' ? (
                          <CellText cell={row.score} onOpen={onOpen} />
                        ) : (
                          <CountCell
                            value={column.key === 'employees' ? row.employees : row.assessed}
                            // One employee to a row: Employees does not apply, and Assessed is a yes or a no.
                            as={rowsBy !== 'employee' ? 'count' : column.key === 'employees' ? 'na' : 'tick'}
                          />
                        )}
                      </Box>
                    ))}
                    {row.cells.map((cell, c) => (
                      <Box
                        key={columns[c].key}
                        sx={{ ...CENTRED, borderBottom: rule, borderLeft: c > 0 ? GRID_LINE : 'none', p: '9px 8px' }}
                      >
                        <CellText cell={cell} onOpen={onOpen} />
                      </Box>
                    ))}
                  </React.Fragment>
                );
              })}
            </Box>
          </Box>
          {/* Only the capabilities move, so the bar runs under them alone. */}
          <PinnedScrollbar scroller={scroller} left={FIRST_COL + pinnedWidth} />
        </Box>
      )}
      {/* The breakdown behind a %, as the dashboard gives it. */}
      <Popover
        open={Boolean(opened)}
        anchorEl={opened?.anchor}
        onClose={() => setOpened(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        PaperProps={{
          sx: {
            mt: '6px',
            minWidth: 280,
            maxWidth: 'min(380px, 92vw)',
            maxHeight: '70vh',
            // The dashboard tooltip's card (DosenTooltip): the same surface, hairline, corners and shadow.
            p: '10px 12px',
            // (The app's theme paints every popover black with !important, so these are too.)
            background: '#232323 !important',
            border: '1px solid #2A2A2A !important',
            borderRadius: '8px',
            boxShadow: '0 12px 32px rgba(0,0,0,0.55) !important'
          }
        }}
      >
        {/* Phone: the row's counts, side by side, above the breakdown. */}
        {opened?.counts && opened.counts.length > 0 && (
          <Box sx={{ display: 'flex', gap: '18px', pb: '8px', mb: '8px', borderBottom: GRID_LINE }}>
            {opened.counts.map((count) => (
              <RowCount key={count.label} {...count} />
            ))}
          </Box>
        )}
        {/* Phone: what Dosen has for this score, which the table shows on hover. */}
        {isMobileApp && opened?.versus && (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              gap: '16px',
              pb: '8px',
              mb: '8px',
              borderBottom: GRID_LINE
            }}
          >
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, color: '#B6B6B6' }}>On Dosen</Typography>
            <Typography sx={{ fontFamily: FONT, fontSize: 11.5, fontWeight: 700, color: opened.versus.dosen.color }}>
              {opened.versus.dosen.text}
            </Typography>
          </Box>
        )}
        {opened && breakdown && (
          <CapabilityScoreBreakdown pairs={opened.pairs} teams={breakdown.teams} resultLabel={breakdown.resultLabel} />
        )}
      </Popover>
    </Box>
  );
};

export default LevelMatrix;
